import React, { useState, useEffect, useRef } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { FiAward, FiPlus, FiPrinter, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import { isMobileLayout } from '../utils/deviceLayout';
import {
  getCertificates, getCertificateTemplate, createCertificate, updateCertificate,
  deleteCertificate, previewCertificate, getNomination, getTeamMembers, getAuthToken,
} from '../services/api';

const AWARD_TITLES = [
  'Excellence in the Field Award',
  'Leadership Through Service Award',
  'Leadership Through Teamwork Award',
  'Ownership Award',
  'Teamwork Award',
  'Growth / Most Improved Award',
  'Problem Solver Award',
  'Professionalism Award',
];

const EMPTY = {
  certificate_no: '', recipient_id: null, recipient_name: '', award_title: '', body: '',
  quote: '', quote_attribution: '', scripture_ref: '', scripture_text: '', seal_text: '',
  closing_line: '', motto: '', presented_by_name: '', presented_by_title: '',
  award_date: '', nomination_id: null,
};

// award_date is a db.Date (date-only). Pin it to local noon so it never renders as the
// day before in Eastern -- never append 'Z' to it.
const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString() : '');

function Certificates() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [mobile] = useState(isMobileLayout());
  const [certs, setCerts] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const previewTimer = useRef(null);

  const isAdmin = !!user?.can_manage_payroll;

  const loadList = async () => {
    try {
      const res = await getCertificates();
      setCerts(res.data.certificates || []);
    } catch (err) {
      console.error('Failed to load certificates', err);
    }
  };

  // A new certificate starts from the template (house wording + next number), then the
  // nomination it came from fills in who, which award, and the nominator's words.
  const startNew = async (nominationId) => {
    setError('');
    setSavedNote('');
    setEditingId(null);
    try {
      const tpl = (await getCertificateTemplate()).data;
      let next = { ...EMPTY, ...tpl };
      if (nominationId) {
        const n = (await getNomination(nominationId)).data;
        next = {
          ...next,
          nomination_id: n.id,
          recipient_id: n.nominee_id,
          recipient_name: n.nominee_name || '',
          award_title: `${n.category} Award`,
          quote: n.reason || '',
          quote_attribution: n.nominator_name ? `${n.nominator_name}, nominator` : '',
        };
      }
      setForm(next);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start a new certificate');
    }
  };

  const openExisting = (cert) => {
    setError('');
    setSavedNote('');
    setEditingId(cert.id);
    setForm({ ...EMPTY, ...cert });
  };

  useEffect(() => {
    if (!isAdmin) return;
    loadList();
    getTeamMembers().then((r) => setMembers(r.data?.team_members || [])).catch(() => {});
    startNew(searchParams.get('nomination'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Live preview, debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    if (!form) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await previewCertificate(form);
        setPreviewHtml(res.data);
      } catch (err) {
        console.error('Preview failed', err);
      }
    }, 400);
    return () => clearTimeout(previewTimer.current);
  }, [form]);

  if (user && !isAdmin) {
    return <Navigate to="/nominations" replace />;
  }

  const set = (field) => (e) => {
    const value = e.target.value;
    setSavedNote('');
    setForm((f) => ({ ...f, [field]: value }));
  };

  const pickRecipient = (e) => {
    const id = e.target.value ? Number(e.target.value) : null;
    const m = members.find((x) => x.id === id);
    setSavedNote('');
    setForm((f) => ({ ...f, recipient_id: id, recipient_name: m ? m.name : f.recipient_name }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const res = editingId
        ? await updateCertificate(editingId, form)
        : await createCertificate(form);
      setEditingId(res.data.id);
      setForm({ ...EMPTY, ...res.data });
      setSavedNote('Saved');
      loadList();
      return res.data;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save certificate');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const print = async () => {
    const saved = await save();
    if (!saved) return;
    const token = await getAuthToken();
    // Token in the query string: the new tab can't send the axios Authorization header.
    window.open(`${API_URL}/certificates/${saved.id}/print?token=${encodeURIComponent(token)}`, '_blank');
  };

  const remove = async () => {
    if (!editingId) return;
    if (!window.confirm(`Delete certificate ${form.certificate_no || ''} for ${form.recipient_name}?`)) return;
    try {
      await deleteCertificate(editingId);
      await loadList();
      startNew(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete certificate');
    }
  };

  return (
    <div style={{ padding: mobile ? '12px' : '24px', maxWidth: '1500px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '16px' }}>
        <div>
          <Link to="/nominations/admin" style={{ color: '#1a73e8', fontSize: '13px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <FiArrowLeft /> Recognition Admin
          </Link>
          <h1 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiAward /> Recognition Certificates
          </h1>
        </div>
        <button onClick={() => startNew(null)} style={btnSecondary}>
          <FiPlus style={{ verticalAlign: '-2px' }} /> New Certificate
        </button>
      </div>

      {certs.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '14px' }}>
          {certs.map((c) => (
            <button
              key={c.id}
              onClick={() => openExisting(c)}
              style={{
                flex: '0 0 auto', textAlign: 'left', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                border: '1px solid ' + (editingId === c.id ? '#1a73e8' : '#e5e7eb'),
                backgroundColor: editingId === c.id ? '#eff6ff' : 'white',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px' }}>{c.recipient_name}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {c.certificate_no} · {fmtDate(c.award_date)}
              </div>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {form && (
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '400px 1fr', gap: '16px', alignItems: 'start' }}>
          <div style={panel}>
            <Section title="Recipient">
              <Field label="Team member">
                <select value={form.recipient_id || ''} onChange={pickRecipient} style={input}>
                  <option value="">— Choose —</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </Field>
              <Field label="Name as printed">
                <input value={form.recipient_name || ''} onChange={set('recipient_name')} style={input} />
              </Field>
              <Field label="Award title">
                <input value={form.award_title || ''} onChange={set('award_title')} list="award-titles" style={input} />
                <datalist id="award-titles">
                  {AWARD_TITLES.map((t) => <option key={t} value={t} />)}
                </datalist>
              </Field>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Field label="Certificate No." style={{ flex: 1 }}>
                  <input value={form.certificate_no || ''} onChange={set('certificate_no')} style={input} />
                </Field>
                <Field label="Date" style={{ flex: 1 }}>
                  <input type="date" value={form.award_date || ''} onChange={set('award_date')} style={input} />
                </Field>
              </div>
            </Section>

            <Section title="Wording">
              <Field label="Citation" hint="Each new line becomes its own paragraph.">
                <textarea value={form.body || ''} onChange={set('body')} rows={7} style={textarea}
                  placeholder="What they did, in a few sentences…" />
              </Field>
              <Field label="Quote">
                <textarea value={form.quote || ''} onChange={set('quote')} rows={3} style={textarea} />
              </Field>
              <Field label="Quote attribution">
                <input value={form.quote_attribution || ''} onChange={set('quote_attribution')} style={input}
                  placeholder="e.g. Mike Bunch, nominator" />
              </Field>
              <Field label="Closing line">
                <input value={form.closing_line || ''} onChange={set('closing_line')} style={input} />
              </Field>
            </Section>

            <Section title="Carried forward" hint="New certificates copy these from the last one you saved.">
              <Field label="Scripture reference">
                <input value={form.scripture_ref || ''} onChange={set('scripture_ref')} style={input} />
              </Field>
              <Field label="Scripture text">
                <textarea value={form.scripture_text || ''} onChange={set('scripture_text')} rows={2} style={textarea} />
              </Field>
              <Field label="Seal text" hint="Each line here is a line on the seal.">
                <textarea value={form.seal_text || ''} onChange={set('seal_text')} rows={4} style={textarea} />
              </Field>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Field label="Presented by" style={{ flex: 1 }}>
                  <input value={form.presented_by_name || ''} onChange={set('presented_by_name')} style={input} />
                </Field>
                <Field label="Title" style={{ flex: 1 }}>
                  <input value={form.presented_by_title || ''} onChange={set('presented_by_title')} style={input} />
                </Field>
              </div>
            </Section>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={print} disabled={saving} style={btnPrimary}>
                <FiPrinter style={{ verticalAlign: '-2px' }} /> Save &amp; Print
              </button>
              <button onClick={save} disabled={saving} style={btnSecondary}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              {editingId && (
                <button onClick={remove} style={{ ...btnSecondary, color: '#b91c1c' }} title="Delete certificate">
                  <FiTrash2 style={{ verticalAlign: '-2px' }} />
                </button>
              )}
              {savedNote && <span style={{ color: '#166534', fontSize: '13px' }}>{savedNote}</span>}
            </div>
          </div>

          <PreviewFrame html={previewHtml} />
        </div>
      )}
    </div>
  );
}

// The certificate is a fixed 11 x 8.5 in sheet; scale the iframe to whatever width
// the column has instead of letting it scroll sideways.
function PreviewFrame({ html }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.6);
  const SHEET_W = 1100;
  const SHEET_H = 860;

  useEffect(() => {
    const measure = () => {
      if (wrapRef.current) setScale(Math.min(1, wrapRef.current.clientWidth / SHEET_W));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <div ref={wrapRef} style={{ ...panel, padding: 0, overflow: 'hidden', height: SHEET_H * scale, position: 'sticky', top: '16px' }}>
      <iframe
        title="Certificate preview"
        srcDoc={html}
        style={{ width: SHEET_W, height: SHEET_H, border: 0, transform: `scale(${scale})`, transformOrigin: '0 0' }}
      />
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div style={{ marginBottom: '18px' }}>
      <div style={{ fontWeight: 700, fontSize: '14px', color: '#14284b', marginBottom: hint ? '2px' : '8px' }}>{title}</div>
      {hint && <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>{hint}</div>}
      {children}
    </div>
  );
}

function Field({ label, hint, style, children }) {
  return (
    <div style={{ marginBottom: '10px', ...style }}>
      <div style={labelStyle}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{hint}</div>}
    </div>
  );
}

const panel = { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '18px' };
const labelStyle = { fontSize: '12px', color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' };
const input = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
const textarea = { ...input, resize: 'vertical', fontFamily: 'inherit' };
const btnPrimary = { padding: '9px 16px', backgroundColor: '#14284b', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '9px 16px', backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };

export default Certificates;
